import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Plus, KeyRound, Power, Trash2, Pencil, ExternalLink, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import SalesPaletteEditor from './SalesPaletteEditor';
import SalesPricingEditor from './SalesPricingEditor';
import SalesContractEditor from './SalesContractEditor';
import SalesJobRequestsPanel from './SalesJobRequestsPanel';
import SalesCommissionsPanel from './SalesCommissionsPanel';
import SalesEmailSettingsPanel from './SalesEmailSettingsPanel';
import { RepAvatar, SalesUserPhoto } from './SalesUserPhoto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales-users`;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
const emptyForm = { username: '', password: '', full_name: '', email: '', phone: '', role: 'rep', language: 'pt-BR' };

const L = {
  'pt-BR': {
    title: 'Usuários do Portal de Vendas', hint: 'Login separado dos instaladores. Acesso em', create: 'Novo vendedor', edit: 'Editar', reset: 'Reset senha', activate: 'Ativar', deactivate: 'Desativar', remove: 'Apagar',
    username: 'Usuário', password: 'Senha', fullName: 'Nome completo', email: 'E-mail', phone: 'Telefone', role: 'Perfil', rep: 'Vendedor', manager: 'Gerente (vê tudo)', lang: 'Idioma',
    save: 'Salvar', cancel: 'Cancelar', created: 'Vendedor criado', saved: 'Salvo', removed: 'Apagado', confirmRemove: (u) => `Apagar o usuário ${u}? Os leads dele continuam no sistema.`, newPwd: 'Nova senha para', pwdDone: 'Senha redefinida',
    empty: 'Nenhum vendedor cadastrado ainda', active: 'ativo', inactive: 'inativo', lastLogin: 'último acesso', never: 'nunca acessou', copy: 'Copiar link', copied: 'Link copiado',
  },
  en: {
    title: 'Sales Portal Users', hint: 'Separate login from installers. Access at', create: 'New sales rep', edit: 'Edit', reset: 'Reset password', activate: 'Activate', deactivate: 'Deactivate', remove: 'Delete',
    username: 'Username', password: 'Password', fullName: 'Full name', email: 'Email', phone: 'Phone', role: 'Role', rep: 'Sales rep', manager: 'Manager (sees all)', lang: 'Language',
    save: 'Save', cancel: 'Cancel', created: 'Sales rep created', saved: 'Saved', removed: 'Deleted', confirmRemove: (u) => `Delete user ${u}? Their leads stay in the system.`, newPwd: 'New password for', pwdDone: 'Password reset',
    empty: 'No sales reps yet', active: 'active', inactive: 'inactive', lastLogin: 'last login', never: 'never logged in', copy: 'Copy link', copied: 'Link copied',
  },
};

const SalesUsersPanel = ({ language = 'pt-BR' }) => {
  const t = L[language] || L['pt-BR'];
  const [section, setSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const portalUrl = `${window.location.origin}/sales`;

  const load = useCallback(async () => {
    try { const { data } = await axios.get(API, auth()); setUsers(data); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...emptyForm, ...u, password: '' }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { full_name, email, phone, role, language: lang } = form;
        await axios.put(`${API}/${editing.id}`, { full_name, email: email || null, phone: phone || null, role, language: lang }, auth());
        toast.success(t.saved);
      } else {
        await axios.post(API, { ...form, email: form.email || null, phone: form.phone || null }, auth());
        toast.success(t.created);
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  const resetPwd = async (u) => {
    const pwd = window.prompt(`${t.newPwd} ${u.username}:`);
    if (!pwd) return;
    try { await axios.post(`${API}/${u.id}/reset-password`, { new_password: pwd }, auth()); toast.success(t.pwdDone); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const toggleActive = async (u) => {
    try { await axios.put(`${API}/${u.id}`, { is_active: !u.is_active }, auth()); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const remove = async (u) => {
    if (!window.confirm(t.confirmRemove(u.username))) return;
    try { await axios.delete(`${API}/${u.id}`, auth()); toast.success(t.removed); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const SECTIONS = [['users', language === 'en' ? 'Users' : 'Vendedores'], ['jobs', language === 'en' ? 'Sold projects' : 'Projetos vendidos'], ['commissions', language === 'en' ? 'Commissions' : 'Comissões'], ['pricing', language === 'en' ? 'Prices' : 'Preços'], ['palette', language === 'en' ? 'Colors' : 'Cores IA'], ['contract', language === 'en' ? 'Contract' : 'Contrato'], ['emails', language === 'en' ? 'Emails' : 'E-mails']];

  return (
    <div className="space-y-4" data-testid="sales-users-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border bg-orange-50/60 border-orange-200">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-orange-600 text-white flex items-center justify-center shrink-0"><Briefcase className="h-4 w-4" /></div>
          <div>
            <p className="font-semibold text-sm">{t.title}</p>
            <p className="text-xs text-slate-600">{t.hint} <a href={portalUrl} target="_blank" rel="noreferrer" className="text-orange-700 font-semibold underline inline-flex items-center gap-1" data-testid="sales-portal-link">{portalUrl} <ExternalLink className="h-3 w-3" /></a></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs" onClick={() => { navigator.clipboard?.writeText(portalUrl); toast.success(t.copied); }} data-testid="sales-portal-copy-link"><Copy className="h-3.5 w-3.5 mr-1" /> {t.copy}</Button>
          <Button size="sm" className="text-xs bg-orange-600 hover:bg-orange-700" onClick={openCreate} data-testid="sales-user-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> {t.create}</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b" data-testid="sales-admin-sections">
        {SECTIONS.map(([k, l]) => <button key={k} type="button" onClick={() => setSection(k)} data-testid={`sales-admin-section-${k}`} className={`px-3 h-9 text-xs font-semibold border-b-2 -mb-px transition-colors ${section === k ? 'border-orange-600 text-orange-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{l}</button>)}
      </div>

      {section === 'users' && (
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg bg-white gap-3" data-testid={`sales-user-row-${u.username}`}>
            <div className="flex-1 min-w-0 flex items-center gap-3">
            <RepAvatar user={u} />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm sm:text-base flex items-center gap-2 flex-wrap">
                {u.username}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${u.role === 'manager' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>{u.role === 'manager' ? t.manager : t.rep}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{u.is_active ? t.active : t.inactive}</span>
              </div>
              <div className="text-slate-600 text-xs sm:text-sm">{u.full_name}{u.email ? ` · ${u.email}` : ''}{u.phone ? ` · ${u.phone}` : ''}</div>
              <div className="text-[11px] text-slate-400">{u.last_login_at ? `${t.lastLogin}: ${u.last_login_at.slice(0, 16).replace('T', ' ')}` : t.never}</div>
            </div>
            </div>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <Button size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => openEdit(u)} data-testid={`sales-user-edit-${u.username}`}><Pencil className="h-3 w-3 mr-1" />{t.edit}</Button>
              <Button size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => resetPwd(u)} data-testid={`sales-user-reset-${u.username}`}><KeyRound className="h-3 w-3 mr-1" />{t.reset}</Button>
              <Button size="sm" variant="outline" className="text-xs px-2 py-1" onClick={() => toggleActive(u)} data-testid={`sales-user-toggle-${u.username}`}><Power className="h-3 w-3 mr-1" />{u.is_active ? t.deactivate : t.activate}</Button>
              <Button size="sm" variant="outline" className="text-xs px-2 py-1 text-red-600 hover:text-red-700" onClick={() => remove(u)} data-testid={`sales-user-delete-${u.username}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
        {users.length === 0 && <p className="text-center text-slate-500 py-6" data-testid="sales-users-empty">{t.empty}</p>}
      </div>
      )}

      {section === 'jobs' && <SalesJobRequestsPanel language={language} />}
      {section === 'commissions' && <SalesCommissionsPanel language={language} />}
      {section === 'pricing' && <SalesPricingEditor language={language} />}
      {section === 'palette' && <SalesPaletteEditor language={language} />}
      {section === 'contract' && <SalesContractEditor language={language} />}
      {section === 'emails' && <SalesEmailSettingsPanel language={language} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="sales-user-dialog">
          <DialogHeader><DialogTitle>{editing ? `${t.edit} · ${editing.username}` : t.create}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t.username} *</Label><Input data-testid="sales-user-username-input" value={form.username} onChange={(e) => set('username', e.target.value)} required autoCapitalize="none" /></div>
                <div><Label>{t.password} *</Label><Input data-testid="sales-user-password-input" type="text" value={form.password} onChange={(e) => set('password', e.target.value)} required minLength={4} /></div>
              </div>
            )}
            <div><Label>{t.fullName} *</Label><Input data-testid="sales-user-fullname-input" value={form.full_name} onChange={(e) => set('full_name', e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t.email}</Label><Input data-testid="sales-user-email-input" type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></div>
              <div><Label>{t.phone}</Label><Input data-testid="sales-user-phone-input" value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t.role}</Label>
                <select data-testid="sales-user-role-select" className="w-full border rounded-md h-10 px-2 text-sm" value={form.role} onChange={(e) => set('role', e.target.value)}>
                  <option value="rep">{t.rep}</option>
                  <option value="manager">{t.manager}</option>
                </select>
              </div>
              <div>
                <Label>{t.lang}</Label>
                <select data-testid="sales-user-lang-select" className="w-full border rounded-md h-10 px-2 text-sm" value={form.language} onChange={(e) => set('language', e.target.value)}>
                  <option value="pt-BR">Português</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
            {editing && <SalesUserPhoto user={editing} language={language} onChanged={(u) => { setEditing(u); load(); }} />}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t.cancel}</Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="sales-user-submit">{t.save}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesUsersPanel;
